import useAuth from "./hooks/useAuth";
import { useEffect, useState } from "react";
import CategoryDropdownScores from "./CategoryDropdownScores";

const CheckUserSentencesSnapshot = ({ language, date, checkuser }) => {
    const { auth, setAuth } = useAuth();
    const [stats, setStats] = useState([]);
    const [scoreType, setScoreType] = useState('AccuracyScore');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    useEffect(() => { 
        setCurrentPage(1); // Reset to first page when filters change
        getMyStats();
    }, [language, date, scoreType]);

    const getMyStats = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        
        try {
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/${scoreType}/sentences`;
            
            const params = new URLSearchParams();
            if (date !== 'All records') params.append('date', date);
            if (checkuser) params.append('checkuser', checkuser);
            
            if (params.toString()) {
                query += `?${params.toString()}`;
            }

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setStats(json || []);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching sentence stats:", error);
        } finally {
            setLoading(false);
        }
    };

    // Pagination calculations
    const totalItems = stats.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentStats = stats.slice(startIndex, endIndex);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading sentences...</p>
                </div>
            </div>
        );
    }

    if (!stats.length) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">📝</div>
                <p className="text-gray-500">No sentence data available</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full space-y-6">
            {/* Header */}
            <div className="text-center">
                <h2 className="text-2xl font-bold text-blue-700">Sentence Performance Snapshot</h2>
                <p className="text-gray-600 mt-2">
                    Detailed analysis of individual sentence performances
                </p>
            </div>

            {/* Controls Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800">Analysis Settings</h3>
                        <p className="text-gray-600 text-sm">View and analyze your sentence performances</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Score Metric
                            </label>
                            <CategoryDropdownScores 
                                selectHandler={e => setScoreType(e.value)} 
                                elValue={scoreType} 
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Pagination Controls - Top */}
            {stats.length > 0 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm text-gray-600">
                        Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} sentences
                    </div>
                    
                    <div className="flex items-center gap-4">
                        {/* Items per page selector */}
                        <select 
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-3 py-1 bg-white"
                        >
                            <option value={5}>5 per page</option>
                            <option value={10}>10 per page</option>
                            <option value={20}>20 per page</option>
                            <option value={50}>50 per page</option>
                        </select>

                        {/* Page navigation */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                            >
                                ←
                            </button>
                            
                            <span className="text-sm text-gray-700 min-w-[80px] text-center">
                                Page {currentPage} of {totalPages}
                            </span>
                            
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                            >
                                →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sentences Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {currentStats.map((sentence, i) => (
                    <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-all duration-200">
                        {/* Sentence Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-800">Sentence {startIndex + i + 1}</h3>
                                <p className="text-gray-500 text-sm">{sentence.date}</p>
                            </div>
                            <button 
                                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium text-sm"
                                onClick={() => {
                                    const snap = new Audio();
                                    if (sentence.audio) {
                                        snap.src = `${process.env.REACT_APP_USER_API_URL}/${sentence.audio}`;
                                        snap.load();
                                        snap.play();
                                    }
                                }}
                            >
                                <span>🔊</span>
                                Play Audio
                            </button>
                        </div>

                        {/* Main Sentence */}
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-lg text-blue-800 font-medium leading-relaxed">
                                {sentence.assessment?.DisplayText || 'No text available'}
                            </p>
                        </div>

                        {/* Word-by-Word Breakdown */}
                        {sentence.assessment?.hasOwnProperty('NBest') && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Word Analysis</h4>
                                <div className="flex flex-wrap gap-2">
                                    {sentence.assessment.NBest[0].Words.map((word, wordIndex) => (
                                        <div 
                                            key={wordIndex}
                                            className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                                                word.PronunciationAssessment?.AccuracyScore >= 80 
                                                    ? "bg-green-50 border-green-200" 
                                                    : word.PronunciationAssessment?.AccuracyScore >= 40 
                                                    ? "bg-yellow-50 border-yellow-200" 
                                                    : "bg-red-50 border-red-200"
                                            }`}
                                        >
                                            <span className="text-sm font-medium text-gray-800 mb-1">
                                                {word.Word}
                                            </span>
                                            <span className="text-xs font-semibold text-blue-600 mb-2">
                                                {word.PronunciationAssessment?.AccuracyScore}%
                                            </span>
                                            
                                            {/* Phonemes Visualization */}
                                            {word.hasOwnProperty("Phonemes") && (
                                                <div className="flex gap-1">
                                                    {word.Phonemes.map((ph, phIndex) => (
                                                        <div 
                                                            key={phIndex}
                                                            className={`w-3 h-3 rounded-full border ${
                                                                ph.PronunciationAssessment?.AccuracyScore >= 80 
                                                                    ? "bg-green-400 border-green-500" 
                                                                    : ph.PronunciationAssessment?.AccuracyScore >= 40 
                                                                    ? "bg-yellow-400 border-yellow-500" 
                                                                    : "bg-red-400 border-red-500"
                                                            }`}
                                                            title={`${ph.Phoneme || ''} (${ph.PronunciationAssessment?.AccuracyScore || 0}%)`}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Score Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-center">
                                <div className="text-sm text-green-700 font-medium">Accuracy</div>
                                <div className="text-lg font-bold text-green-800">{sentence.AccuracyScore}%</div>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200 text-center">
                                <div className="text-sm text-blue-700 font-medium">Fluency</div>
                                <div className="text-lg font-bold text-blue-800">{sentence.FluencyScore}%</div>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 text-center">
                                <div className="text-sm text-purple-700 font-medium">Completeness</div>
                                <div className="text-lg font-bold text-purple-800">{sentence.CompletenessScore}%</div>
                            </div>
                            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200 text-center">
                                <div className="text-sm text-orange-700 font-medium">Pronunciation</div>
                                <div className="text-lg font-bold text-orange-800">{sentence.PronScore}%</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Pagination Controls - Bottom */}
            {stats.length > 0 && totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 pt-6 border-t border-gray-200">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-sm"
                    >
                        First
                    </button>
                    
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                        ← Previous
                    </button>
                    
                    <span className="text-sm text-gray-700">
                        Page {currentPage} of {totalPages}
                    </span>
                    
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                        Next →
                    </button>
                    
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-sm"
                    >
                        Last
                    </button>
                </div>
            )}

            {/* Legend */}
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Score Legend</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                        <span className="text-gray-600">80-100% (Excellent)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <span className="text-gray-600">40-80% (Good)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <span className="text-gray-600">0-40% (Needs Practice)</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CheckUserSentencesSnapshot;